import axios from "axios";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  ArrowLeft,
  Check,
  Gift,
  Loader2,
  MapPin,
  Minus,
  Plus,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";

/* Template palette */
const c = {
  greenDk: "#1C4A2E",
  greenMd: "#2D6B45",
  greenLt: "#4A9B6F",
  gold: "#E8A020",
  red: "#E84040",
  redLt: "#FFF0F0",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
};

interface CartItem {
  cart_premio_id: number;
  premio: string;
  cantidad: number;
  usuario_cart_id: number;
  puntos: number;
  premio_id?: number;
  categoria?: string;
}

interface UsuarioEntrega {
  farmacia_nombre?: string;
  pais?: string;
  canton?: string;
  distrito?: string;
  barrio?: string;
  direccion?: string;
}

function formatEntregaLines(u: UsuarioEntrega): string[] {
  const lines: string[] = [];
  if (u.farmacia_nombre?.trim()) lines.push(u.farmacia_nombre.trim());
  if (u.direccion?.trim()) lines.push(u.direccion.trim());
  const parts: string[] = [];
  if (u.barrio?.trim()) parts.push(`Barrio ${u.barrio.trim()}`);
  if (u.distrito?.trim()) parts.push(u.distrito.trim());
  if (u.canton?.trim()) parts.push(`Cantón ${u.canton.trim()}`);
  if (parts.length) lines.push(parts.join(" · "));
  if (u.pais?.trim() && (lines.length > 0 || u.pais !== "Costa Rica")) {
    lines.push(u.pais.trim());
  } else if (u.pais?.trim() === "Costa Rica" && lines.length > 0) {
    lines.push("Costa Rica");
  }
  return lines;
}

function hasEntregaData(u: UsuarioEntrega | null): u is UsuarioEntrega {
  return u != null && formatEntregaLines(u).length > 0;
}

function detailLinesAfterHeadline(
  u: UsuarioEntrega
): { showFarmaciaHeadline: boolean; lines: string[] } {
  const all = formatEntregaLines(u);
  const showFarmaciaHeadline = Boolean(u.farmacia_nombre?.trim());
  if (!showFarmaciaHeadline) {
    return { showFarmaciaHeadline: false, lines: all };
  }
  const name = u.farmacia_nombre?.trim() ?? "";
  if (all[0] === name) {
    return { showFarmaciaHeadline: true, lines: all.slice(1) };
  }
  return { showFarmaciaHeadline: true, lines: all };
}

interface Props {
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
  cartView: boolean;
  setCartView: React.Dispatch<React.SetStateAction<boolean>>;
  availablePoints: number;
  setView: Dispatch<SetStateAction<string>>;
}

function formatPts(n: number) {
  return n.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function emojiForName(name: string) {
  const s = name.trim();
  if (/tablet|celular|phone|tech|laptop/i.test(s)) return "📱";
  if (/super|vale|mercado|compra/i.test(s)) return "🛒";
  if (/coffee|café|taza/i.test(s)) return "☕";
  return "🎁";
}

const Cart = ({
  cartView,
  setCartView,
  availablePoints,
  counter,
  setCounter,
  setView,
}: Props) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [deliveryUser, setDeliveryUser] = useState<UsuarioEntrega | null>(
    null
  );
  /** `idle` after cart closes; while cart is open, `ready` only after the last address fetch finished. */
  const [deliveryAddressStatus, setDeliveryAddressStatus] = useState<
    "idle" | "ready"
  >("idle");
  /** Avoid showing "carrito vacío" while the first `getCartItems` is still in flight. */
  const [cartListStatus, setCartListStatus] = useState<"idle" | "ready">(
    "idle"
  );
  const [isClosing, setIsClosing] = useState(false);
  const [success, setSuccess] = useState<{
    order: string;
    total: number;
    count: number;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const userId = sessionStorage.getItem("user_id");
  const token = sessionStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ "auth-token": token ?? "" }),
    [token]
  );

  const getCartItems = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/carrito/${userId}`,
        { headers: authHeader }
      );
      const data = response.data;
      setCartItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
    }
  }, [userId, authHeader]);

  const getDeliveryProfile = useCallback(async () => {
    if (!userId) {
      setDeliveryUser(null);
      setDeliveryAddressStatus("ready");
      return;
    }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/usuarios/${userId}`,
        { headers: authHeader }
      );
      setDeliveryUser(res.data as UsuarioEntrega);
    } catch {
      setDeliveryUser(null);
    } finally {
      setDeliveryAddressStatus("ready");
    }
  }, [userId, authHeader]);

  const removeLineApi = useCallback(
    async (cartPremioId: number) => {
      if (!userId) return;
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/carrito/${userId}/${cartPremioId}`,
        { headers: authHeader, params: { premio_id: cartPremioId } }
      );
    },
    [userId, authHeader]
  );

  const deleteCartItem = async (cartPremioId: number) => {
    try {
      await removeLineApi(cartPremioId);
      setCartItems((prev) =>
        prev.filter((item) => item.cart_premio_id !== cartPremioId)
      );
    } catch (error) {
      console.log(error);
    }
  };

  const addOne = async (premioId: number) => {
    if (!userId) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/carrito/${userId}/${premioId}`,
        {},
        { headers: authHeader }
      );
      await getCartItems();
    } catch (e) {
      console.log(e);
      Swal.fire({
        icon: "error",
        title: "No se pudo actualizar",
        text: "Intenta de nuevo.",
      });
    }
  };

  const changeQty = async (item: CartItem, delta: number) => {
    const premioId = item.premio_id;
    if (delta > 0) {
      if (premioId == null) {
        Swal.fire({
          icon: "info",
          text: "No se puede ajustar la cantidad desde el catálogo; vuelve a agregar el premio.",
        });
        return;
      }
      await addOne(premioId);
      return;
    }
    if (item.cantidad <= 1) {
      await deleteCartItem(item.cart_premio_id);
      return;
    }
    if (premioId == null) {
      Swal.fire({
        icon: "info",
        text: "Quita el ítem y vuelve a añadirlo con la cantidad deseada.",
      });
      return;
    }
    try {
      const n = item.cantidad - 1;
      await removeLineApi(item.cart_premio_id);
      for (let i = 0; i < n; i++) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/carrito/${userId}/${premioId}`,
          {},
          { headers: authHeader }
        );
      }
      await getCartItems();
    } catch (e) {
      console.log(e);
      await getCartItems();
    }
  };

  const clearCart = async () => {
    const ok = await Swal.fire({
      title: "¿Vaciar el carrito?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, vaciar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    for (const item of [...cartItems]) {
      try {
        await removeLineApi(item.cart_premio_id);
      } catch (e) {
        console.log(e);
      }
    }
    setCartItems([]);
    await getCartItems();
  };

  const createPedido = async () => {
    if (cartItems.length === 0) {
      return Swal.fire({ icon: "error", title: "No hay premios en el carrito" });
    }
    const total = cartItems.reduce(
      (acc, item) => acc + item.cantidad * item.puntos,
      0
    );
    if (total > availablePoints) {
      return Swal.fire({
        icon: "error",
        title: "No tienes suficientes puntos",
      });
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pedidos/new`,
        { usuario_id: userId },
        { headers: authHeader }
      );
      const orderNum =
        (res.data as { id?: string | number })?.id != null
          ? String((res.data as { id: string | number }).id)
          : `ORD-${Date.now().toString().slice(-6)}`;
      setSuccess({
        order: orderNum,
        total,
        count: cartItems.reduce((a, i) => a + i.cantidad, 0),
      });
      setCartItems([]);
      setCounter(counter + 1);
      setCartView(false);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 404 &&
        error.response.data?.unavailablePremios
      ) {
        const unfoundProducts = error.response.data.unavailablePremios;
        const rows = unfoundProducts
          .map((p: { nombre: string }) => `<tr><td>${p.nombre}</td></tr>`)
          .join("");
        Swal.fire({
          title: "Productos no disponibles",
          icon: "warning",
          html: `<table class="table"><tbody>${rows}</tbody></table>`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo completar el canje. Intenta de nuevo.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeCart = () => {
    if (!cartView) return;
    setIsClosing(true);
    setTimeout(() => {
      setCartView(false);
      setIsClosing(false);
    }, 300);
  };

  const totalPts = useMemo(
    () =>
      cartItems.reduce((s, i) => s + i.cantidad * i.puntos, 0),
    [cartItems]
  );
  const afterPts = availablePoints - totalPts;
  const canBuy = afterPts >= 0;
  const countItems = useMemo(
    () => cartItems.reduce((s, i) => s + i.cantidad, 0),
    [cartItems]
  );

  useLayoutEffect(() => {
    if (cartView) {
      setCartListStatus("idle");
    }
  }, [cartView]);

  useEffect(() => {
    if (cartView === false) {
      setDeliveryAddressStatus("idle");
      setCartListStatus("idle");
      setTimeout(() => {
        setCartItems([]);
      }, 500);
    } else {
      void (async () => {
        try {
          await getCartItems();
        } finally {
          setCartListStatus("ready");
        }
      })();
      getDeliveryProfile();
    }
  }, [cartView, getCartItems, getDeliveryProfile]);

  if (success) {
    return (
        <div
          className="fixed inset-0 z-[960] flex flex-col items-center justify-center gap-4 p-4 text-center sm:p-6 md:p-8"
          style={{ background: c.surface }}
        >
          <div
            className="flex h-28 w-28 items-center justify-center rounded-3xl text-5xl shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${c.greenDk}, ${c.greenLt})`,
            }}
          >
            <Gift className="h-14 w-14 text-white" />
          </div>
          <h2
            className="text-2xl font-extrabold"
            style={{ color: c.ink }}
          >
            ¡Canje exitoso!
          </h2>
          <p className="max-w-sm text-sm leading-relaxed md:max-w-md" style={{ color: c.muted }}>
            Tu pedido fue registrado. Los premios se enviarán a la dirección
            vinculada a tu farmacia o la indicada en el programa.
          </p>
          <div
            className="w-full max-w-sm space-y-2 rounded-2xl border p-4 text-left md:max-w-md"
            style={{ borderColor: c.border, background: c.white }}
          >
            <div className="flex justify-between text-sm">
              <span style={{ color: c.muted }}>Número de orden</span>
              <span className="font-bold" style={{ color: c.ink }}>
                {success.order}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: c.muted }}>Artículos</span>
              <span className="font-bold" style={{ color: c.ink }}>
                {success.count}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2 text-sm" style={{ borderColor: c.border }}>
              <span style={{ color: c.muted }}>Total canjeado</span>
              <span
                className="font-bold"
                style={{ color: c.gold, fontSize: "1.05rem" }}
              >
                ¢ {formatPts(success.total)}
              </span>
            </div>
          </div>
          <div className="mt-2 flex w-full max-w-sm flex-col gap-2 md:max-w-md">
            <button
              type="button"
              className="w-full rounded-2xl py-3.5 text-base font-bold text-white transition hover:opacity-95"
              style={{ background: c.greenMd }}
              onClick={() => {
                setSuccess(null);
                setCartView(false);
              }}
            >
              Volver al inicio
            </button>
            <button
              type="button"
              className="w-full text-sm font-semibold"
              style={{ color: c.muted }}
              onClick={() => {
                setSuccess(null);
                setCartView(false);
              }}
            >
              Ver más premios
            </button>
          </div>
        </div>
    );
  }

  if (!cartView && !isClosing) return null;

  return (
    <>
      <div
        className={`${
          cartView || isClosing
            ? "fixed inset-0 z-[900] flex min-h-0 min-w-0 flex-col bg-[#1a1a1a] md:flex-row md:bg-transparent"
            : "hidden"
        }`}
        aria-hidden={!(cartView || isClosing)}
      >
        <button
          type="button"
          className="hidden min-h-0 min-w-0 flex-1 cursor-default border-0 p-0 md:block"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={closeCart}
          aria-label="Cerrar carrito"
        />
        <div
          className="flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden md:w-[min(42rem,100%)] md:max-w-2xl md:flex-none md:shrink-0 md:rounded-l-2xl md:shadow-2xl"
          style={{ background: c.surface }}
        >
          {/* header */}
          <header
            className="flex shrink-0 items-center justify-between border-b border-black/5 px-3.5 py-3.5"
            style={{ background: c.white }}
          >
            <button
              type="button"
              onClick={closeCart}
              className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border-[1.5px] border-black/10 bg-transparent transition hover:bg-black/5"
              aria-label="Volver"
            >
              <ArrowLeft className="h-5 w-5" style={{ color: c.ink }} />
            </button>
            <h1
              className="text-center text-base font-bold"
              style={{ color: c.ink }}
            >
              Mi carrito{" "}
              {countItems > 0 ? (
                <span
                  className="text-sm font-medium"
                  style={{ color: c.muted }}
                >
                  ({countItems})
                </span>
              ) : null}
            </h1>
            <div className="h-[38px] w-[38px]" aria-hidden />
          </header>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-4"
            style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}
          >
            {cartListStatus !== "ready" ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-3xl"
                  style={{ background: "#E8EDEA" }}
                >
                  <Loader2
                    className="h-11 w-11 animate-spin"
                    style={{ color: c.muted }}
                    aria-hidden
                  />
                </div>
                <p className="text-lg font-extrabold" style={{ color: c.ink }}>
                  Cargando carrito…
                </p>
                <p
                  className="max-w-xs text-sm leading-relaxed"
                  style={{ color: c.muted }}
                >
                  Un momento mientras obtenemos tus premios.
                </p>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-3xl text-4xl"
                  style={{ background: "#E8EDEA" }}
                >
                  <ShoppingCart className="h-12 w-12" style={{ color: c.muted }} />
                </div>
                <p className="text-lg font-extrabold" style={{ color: c.ink }}>
                  Tu carrito está vacío
                </p>
                <p className="max-w-xs text-sm leading-relaxed" style={{ color: c.muted }}>
                  Aún no agregaste premios. Explorá el catálogo y elegí lo que
                  quieras canjear.
                </p>
                <button
                  type="button"
                  className="mt-1 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white transition"
                  style={{ background: c.greenMd }}
                  onClick={closeCart}
                >
                  <Gift className="h-4 w-4" />
                  Ver premios
                </button>
              </div>
            ) : (
              <>
                {/* saldo */}
                <div
                  className="flex items-center justify-between gap-2 rounded-2xl p-4"
                  style={{
                    background: `linear-gradient(135deg, ${c.greenDk}, ${c.greenMd})`,
                    flexShrink: 0,
                  }}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-white"
                      style={{
                        background: c.gold,
                        boxShadow: "0 3px 10px rgba(232,160,32,0.5)",
                      }}
                    >
                      ¢
                    </div>
                    <div>
                      <div
                        className="text-[0.72rem] font-medium"
                        style={{ color: "rgba(255,255,255,0.65)" }}
                      >
                        Saldo disponible
                      </div>
                      <div
                        className="text-2xl font-extrabold leading-tight text-white"
                      >
                        {formatPts(availablePoints)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className="text-[0.68rem]"
                      style={{ color: "rgba(255,255,255,0.55)" }}
                    >
                      Saldo tras canje
                    </div>
                    <div
                      className="text-base font-bold"
                      style={{
                        color:
                          canBuy
                            ? "rgba(255,255,255,0.9)"
                            : "#ff8a8a",
                      }}
                    >
                      {formatPts(afterPts)}
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center justify-between text-xs font-bold uppercase tracking-wider"
                  style={{ color: c.muted }}
                >
                  <span>Premios seleccionados</span>
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-xs font-semibold"
                    style={{ color: c.red }}
                  >
                    Vaciar carrito
                  </button>
                </div>

                <div className="flex flex-col gap-2" role="list" aria-label="Premios en el carrito">
                  {cartItems.map((item, idx) => (
                    <div
                      key={item.cart_premio_id}
                      role="listitem"
                      className="overflow-hidden rounded-2xl border-[1.5px] border-[rgba(28,74,46,0.1)] bg-white transition hover:border-[#4A9B6F]/50 hover:shadow-md"
                      style={{ animation: `cartSlide 0.3s ease both`, animationDelay: `${idx * 0.05}s` }}
                    >
                      <div className="flex items-center gap-3.5 p-3.5">
                        <div
                          className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-3xl"
                          style={{ background: c.surface }}
                        >
                          {emojiForName(item.premio)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-[0.65rem] font-semibold uppercase tracking-wider"
                            style={{ color: c.greenMd }}
                          >
                            {item.categoria ?? "Premio"}
                          </p>
                          <p
                            className="mt-0.5 line-clamp-2 text-sm font-bold leading-snug"
                            style={{ color: c.ink }}
                          >
                            {item.premio}
                          </p>
                          <div
                            className="mt-1.5 flex items-center gap-1 text-sm font-extrabold"
                            style={{ color: c.gold }}
                          >
                            <span
                              className="flex h-4 w-4 items-center justify-center rounded-full text-[0.5rem] font-extrabold text-white"
                              style={{ background: c.gold }}
                            >
                              ¢
                            </span>
                            {formatPts(item.puntos)} por unidad
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteCartItem(item.cart_premio_id)}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition"
                          style={{ background: c.redLt, color: c.red }}
                          aria-label="Quitar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div
                        className="flex items-center justify-between border-t border-[rgba(28,74,46,0.1)] px-3.5 py-2.5"
                        style={{ background: c.surface }}
                      >
                        <span
                          className="text-xs font-medium"
                          style={{ color: c.muted }}
                        >
                          Cantidad
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[rgba(28,74,46,0.1)] bg-white text-lg font-semibold leading-none"
                            style={{ color: c.ink }}
                            disabled={item.cantidad <= 1}
                            onClick={() => changeQty(item, -1)}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span
                            className="min-w-[1.5rem] text-center text-sm font-bold"
                            style={{ color: c.ink }}
                          >
                            {item.cantidad}
                          </span>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[rgba(28,74,46,0.1)] bg-white"
                            style={{ color: c.ink }}
                            onClick={() => changeQty(item, 1)}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <span
                          className="text-sm font-bold"
                          style={{ color: c.greenMd }}
                        >
                          ¢ {formatPts(item.puntos * item.cantidad)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: c.muted, marginTop: 4 }}
                >
                  Envío a
                </div>
                <div
                  className="rounded-2xl border border-[rgba(28,74,46,0.1)] p-4"
                  style={{ background: c.white }}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div
                      className="flex items-center gap-2 text-sm font-bold"
                      style={{ color: c.ink }}
                    >
                      <MapPin className="h-4 w-4" style={{ color: c.greenMd }} />
                      Dirección de entrega
                    </div>
                    <button
                      type="button"
                      className="text-xs font-semibold"
                      style={{ color: c.greenMd }}
                      onClick={() => {
                        setCartView(false);
                        setView("Perfil");
                      }}
                    >
                      Cambiar
                    </button>
                  </div>
                  {deliveryAddressStatus !== "ready" ? (
                    <p
                      className="text-sm font-medium"
                      style={{ color: c.muted }}
                    >
                      Cargando dirección…
                    </p>
                  ) : hasEntregaData(deliveryUser) ? (
                    (() => {
                      const { showFarmaciaHeadline, lines: bodyLines } =
                        detailLinesAfterHeadline(deliveryUser);
                      return (
                        <>
                          <p
                            className="text-sm font-semibold"
                            style={{ color: c.ink }}
                          >
                            {showFarmaciaHeadline
                              ? "Entrega a farmacia registrada"
                              : "Dirección de entrega"}
                          </p>
                          <div
                            className="mt-1 space-y-1 text-sm leading-relaxed"
                            style={{ color: c.ink }}
                          >
                            {bodyLines.map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <p
                      className="text-sm font-semibold"
                      style={{ color: c.ink }}
                    >
                      No tenés una dirección de entrega registrada
                    </p>
                  )}
                </div>

                <div
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: c.muted, marginTop: 4 }}
                >
                  Resumen
                </div>
                <div
                  className="overflow-hidden rounded-2xl border border-[rgba(28,74,46,0.1)]"
                  style={{ background: c.white }}
                >
                  <div
                    className="flex items-center justify-between border-b border-[rgba(28,74,46,0.1)] px-4 py-3.5"
                  >
                    <span
                      className="text-sm"
                      style={{ color: c.muted }}
                    >
                      Artículos ({countItems})
                    </span>
                    <span className="text-sm font-bold" style={{ color: c.ink }}>
                      ¢ {formatPts(totalPts)}
                    </span>
                  </div>
                  <div
                    className="flex items-center justify-between border-b border-[rgba(28,74,46,0.1)] px-4 py-3.5"
                  >
                    <span className="text-sm" style={{ color: c.muted }}>
                      Tu saldo actual
                    </span>
                    <span
                      className="text-sm font-bold"
                      style={{ color: c.gold, fontSize: "1rem" }}
                    >
                      ¢ {formatPts(availablePoints)}
                    </span>
                  </div>
                  <div
                    className="flex items-center justify-between gap-2 px-4 py-3.5"
                    style={{
                      background: `linear-gradient(135deg, ${c.greenDk}, ${c.greenMd})`,
                    }}
                  >
                    <span
                      className="text-sm font-semibold"
                      style={{ color: "rgba(255,255,255,0.7)" }}
                    >
                      Total a descontar
                    </span>
                    <span
                      className="flex items-center gap-1 text-xl font-extrabold text-white"
                    >
                      <span
                        className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-[0.65rem] text-white"
                        style={{ background: c.gold }}
                      >
                        ¢
                      </span>
                      {formatPts(totalPts)}
                    </span>
                  </div>
                </div>

                {!canBuy ? (
                  <div
                    className="flex items-start gap-3 rounded-2xl border px-4 py-3"
                    style={{
                      background: c.redLt,
                      borderColor: "rgba(232,64,64,0.2)",
                    }}
                  >
                    <InfoIcon />
                    <div>
                      <p className="text-sm font-bold" style={{ color: c.red }}>
                        Saldo insuficiente
                      </p>
                      <p
                        className="mt-0.5 text-xs leading-relaxed"
                        style={{ color: "#b03030" }}
                      >
                        Te faltan ¢ {formatPts(Math.abs(afterPts))} para
                        completar este canje.
                      </p>
                    </div>
                  </div>
                ) : null}

                <button
                  type="button"
                  disabled={!canBuy || submitting}
                  onClick={createPedido}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-extrabold text-white shadow-lg transition disabled:cursor-not-allowed disabled:opacity-45"
                  style={{
                    background: c.greenMd,
                    boxShadow: "0 4px 18px rgba(45,107,69,0.4)",
                  }}
                >
                  {submitting ? (
                    "Procesando…"
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      Confirmar canje · ¢ {formatPts(totalPts)}
                    </>
                  )}
                </button>
                <p
                  className="px-2 text-center text-xs leading-relaxed"
                  style={{ color: c.muted }}
                >
                  Al confirmar, los puntos se descontarán de tu saldo según las
                  reglas del programa.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes cartSlide {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>
    </>
  );
};

function InfoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E84040" strokeWidth="2" className="shrink-0">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default Cart;
